import React from 'react';
import { Box } from '@mui/material';
import { AcceptConditionsProps, ExternalSubscriptionStatus } from '../../../types/authorization';
import AlertDialog from '../../../components/AlertDialog';
import AddressForm from '../../../components/AddressForm';
import InsuranceForm from '../../../components/InsuranceForm';
import { getExternalSubscription, getPreApproveConditions } from '../../../queries/authorization';
import LoanRequestConditions from '../../../components/LoanRequestConditions';
import BancameBackdrop from '../../../components/BancameBackdrop';
import analytics from '../../../utils/analytics';
import AddBankAccountDialog from '../../../components/AddBankAccountDialog';
import { getBankAccount } from '../../../queries/account';

export default function AcceptConditions(props: AcceptConditionsProps) {
  const {
    token,
    loanRequest,
    onSuccess,
    onError,
    onUpdateLoanRequest,
    initialStep,
    productType,
  } = props;

  const [close, setClose] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [step, setStep] = React.useState<'ADDRESS' | 'INSURANCES' | 'ACCEPT_CONDITIONS' | 'PAYMENT_SUBSCRIPTION' | 'AUTHORIZATION' | 'ADD_BANK_ACCOUNT' >(
    initialStep === 'ACCEPT_CONDITIONS'
      ? 'ADDRESS' : initialStep,
  );
  const [externalSubscriptionStatus, setExternalSubscriptionStatus] = React
    .useState<ExternalSubscriptionStatus>(undefined);
  const [trigger, setTrigger] = React.useState(false);
  const [hasBankAccount, setHasBankAccount] = React.useState<boolean>(false);
  const [hasPatSubscription, setHasPatSubscription] = React.useState<boolean>(false);

  React.useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setTrigger(true);
    }, 1000);
  }, []);

  const handleNextStep = async () => {
    setLoading(true);
    setTrigger(true);
    switch (step) {
      case 'ADDRESS':
        if (loanRequest.loanRequestInsurances.length > 0) {
          const insurances = loanRequest.loanRequestInsurances.map(
            (insurance) => insurance.insurance,
          );
          const hasFormSchema = insurances.some(
            (insurance) => insurance.formSchema !== null
              && Object.keys(insurance.formSchema).length > 0,
          );
          if (hasFormSchema) {
            setStep('INSURANCES');
          } else {
            setStep(!hasBankAccount && hasPatSubscription ? 'ADD_BANK_ACCOUNT' : 'ACCEPT_CONDITIONS');
          }
        } else {
          setStep(!hasBankAccount && hasPatSubscription ? 'ADD_BANK_ACCOUNT' : 'ACCEPT_CONDITIONS');
        }
        return setLoading(false);

      case 'INSURANCES':
        setStep(!hasBankAccount && hasPatSubscription ? 'ADD_BANK_ACCOUNT' : 'ACCEPT_CONDITIONS');
        return null;
      case 'ADD_BANK_ACCOUNT':
        setStep('ACCEPT_CONDITIONS');
        return null;
      case 'ACCEPT_CONDITIONS':
      case 'PAYMENT_SUBSCRIPTION':
      case 'AUTHORIZATION':
        setLoading(true);
        return onSuccess();

      default:
        return null;
    }
  };

  React.useEffect(() => {
    if (close) {
      setTimeout(() => {
        setClose(false);
      }, 1000);
    }
    if (trigger) {
      setTimeout(() => {
        setTrigger(false);
      }, 1000);
    }
  }, [close, trigger]);

  const getExternalSubscriptionStatus = async () => {
    const { subscriptionStatus, paymentMethod } = await getExternalSubscription(token);
    setExternalSubscriptionStatus(subscriptionStatus);
    const hasPatSubscriptionSet = subscriptionStatus && ['PENDING', 'ACTIVE'].includes(subscriptionStatus) && paymentMethod === 'pat';
    setHasPatSubscription(hasPatSubscriptionSet || false);
  };

  const getHasBankAccount = async () => {
    const { data } = await getBankAccount(token);
    setHasBankAccount(data.bank !== null);
  };

  const acceptConditionsStep = async () => {
    setClose(true);
    setLoading(true);
    const { data } = await getPreApproveConditions(token);
    if (data.type !== 'loan-request') {
      setError(true);
      return;
    }
    onUpdateLoanRequest(data.detail);

    setLoading(false);
  };

  React.useEffect(() => {
    getExternalSubscriptionStatus();
    getHasBankAccount();
  }, []);

  React.useEffect(() => {
    switch (step) {
      case 'ADDRESS':
        analytics.page('LR - CONDICIONES', 'INGRESO DIRECCION');
        break;
      case 'INSURANCES':
        analytics.page('LR - CONDICIONES', 'FORMULARIO SEGUROS');
        break;
      case 'ADD_BANK_ACCOUNT':
        analytics.page('LR - CONDICIONES', 'INGRESO CUENTA BANCARIA');
        break;
      case 'ACCEPT_CONDITIONS':
        analytics.page('LR - CONDICIONES', 'RESUMEN OFERTA');
        acceptConditionsStep();
        break;
      case 'PAYMENT_SUBSCRIPTION':
      case 'AUTHORIZATION':
        analytics.page('LR - REDIRECCION', 'RESUMEN OFERTA');
        setLoading(false);
        break;
      default:
        break;
    }
  }, [step]);

  React.useEffect(() => {
    if (error) {
      onError(500);
    }
  }, [error]);

  const renderStepComponent = (currentStep: string) => {
    switch (currentStep) {
      case 'ADDRESS':
        return (
          <AlertDialog
            isTriggered={trigger}
            triggerClose={close}
            maxWidth="sm"
            hideActions
            dialogContentStyle={{ height: '100%' }}
            dialogContent={(
              <AddressForm
                token={token}
                onNext={handleNextStep}
                onError={() => setError(true)}
              />
)}
            button={<div />}
          />

        );
      case 'INSURANCES':
        return (
          <AlertDialog
            isTriggered={trigger}
            triggerClose={close}
            maxWidth="sm"
            hideActions
            dialogContentStyle={{ height: '100%' }}
            dialogContent={(
              <InsuranceForm
                token={token}
                loanRequest={loanRequest}
                onNext={handleNextStep}
                onError={() => setError(true)}
                buttonLabel={!hasBankAccount && hasPatSubscription ? 'Siguiente' : 'Finalizar'}
              />
)}
            button={<div />}
          />

        );
      case 'ADD_BANK_ACCOUNT':
        return (
          <AddBankAccountDialog
            token={token}
            open={trigger}
            closeTrigger={close}
            onSuccess={handleNextStep}
            isLoanRequest
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: {
        xs: '100%',
        sm: '90%',
        md: '70%',
        lg: '60%',
      },
    }}
    >
      <LoanRequestConditions
        loanRequest={loanRequest}
        externalSubscriptionStatus={externalSubscriptionStatus}
        onNext={handleNextStep}
        productType={productType}
      />
      {loading && <BancameBackdrop openBackdrop={loading} loadingText="Cargando..." type="loading" />}
      {renderStepComponent(step)}
    </Box>

  );
}
